import { CharComp } from '@/components/CharComp.tsx'
import { CollectCoin } from '@/components/CollectCoin.tsx'
import { QuoteSkeleton } from '@/components/QuoteSkeleton'
import { marketTabs, MarketType } from '@/constants/MarketData.ts'
import { PageRoutes } from '@/constants/PageRoutes.ts'
import { useHandleMoreList } from '@/hooks/use-handle-more-list.ts'
import { CustomTableLayout } from '@/layouts/CustomTableLayout'
import '@/layouts/trading-pair-quotes-layout.scss'
import '@/styles/components/mini-quotes.scss'
import { Search } from '@element-plus/icons-vue'
import classNames from 'classnames'
import { useI18n } from 'i18n-utils'
import { useBaseInfoStore, useDoubleContractKlineStore, useQuoteStore, useResponsive } from 'store'
import { InputView, SizedBox, VirtualTable } from 'ui-components'
import type { TableColumnProps } from 'ui-components/src/components/VirtualTable/VirtualTableThCell.tsx'
import { DefineComponentProps, firstCharToUpper, requestIdleCallbackFrame } from 'utils'
import { Tab, Tabs } from 'vant'

type MarketQuoteProps = {
  /** 条件 */
  condition?: (data: ApiResponse.CoinStaticItemData) => boolean
}

type SortType = 'ascending' | 'descending' | 'normal'

export type SortChangeParams = {
  prop: string
  sortType: SortType
}

interface MiniQuotesProps extends MarketQuoteProps {
  onRowClick?: (row: SocketResponse.TradingPairData) => void
}

export const ContractMiniQuotes = defineComponent<MiniQuotesProps>({
  name: 'MiniQuotes',
  props: DefineComponentProps<MiniQuotesProps>(['condition', 'onRowClick']),
  setup(props) {
    const expandKeys = ref<SymbolId[]>([])
    const currentMarketType = ref(MarketType.USDT)
    const currentSortParams = ref<SortChangeParams>()
    const moreData = useHandleMoreList<SocketResponse.TradingPairData>({
      pageSize: 10,
      page: 1,
    })

    const router = useRouter()
    const klineStore = useDoubleContractKlineStore()
    const quoteStore = useQuoteStore()
    const responsive = useResponsive()
    const baseInfoStore = useBaseInfoStore()
    const { t } = useI18n()

    const coinList = computed<SocketResponse.TradingPairData[]>(() => {
      const list = quoteStore.allContractList.filter((item) => {
        const data = baseInfoStore.getContractCoinStaticItemDataBySymbolId(item.symbolId)
        if (!data) return false

        if (currentMarketType.value === MarketType.USDT) {
          return quoteStore.filterContractByKeyword(item)
        }
        if (currentMarketType.value === MarketType.Collect) {
          return quoteStore.collectSymbolIds.includes(data.symbolId) && quoteStore.filterContractByKeyword(item)
        }

        return false
      })
      return getSortData(list)
    })

    const onSwitchMarket = (marketType: MarketType) => {
      currentMarketType.value = marketType
    }

    const handleSearchKeywordChange = useDebounceFn((value: string) => {
      requestIdleCallbackFrame(() => {
        quoteStore.searchKeyword = value
      })
    }, 200)

    const onRowClick = (row: SocketResponse.TradingPairData) => {
      const symbolId = row.symbolId
      props.onRowClick?.(row)
      router.push({
        path: PageRoutes.ContractTrade,
        query: {
          symbol: symbolId,
        },
      })
      klineStore.setSubscribeKlineParams(symbolId, klineStore.currentResolution)
    }

    const columns = computed(() => {
      const list: TableColumnProps<SocketResponse.TradingPairData>[] = [
        {
          prop: 'name',
          label: t('交易对'),
          width: responsive.isMobile ? '200px' : '280px',
          sortable: 'custom',
          render(item) {
            return (
              <div class={'coin-wrap'}>
                <CollectCoin symbolId={item.row.symbolId} class={'collect-coin border-none'} />
                <SizedBox width={4} />
                <img class={'coin-icon'} width={24} src={baseInfoStore.getContractCoinIconUrlBySymbolId(item.row.symbolId)} alt="" />
                <div class={'coin-name-wrap'}>
                  <div class={'coin-name'}>
                    <CharComp class={'from'}>{baseInfoStore.getContractSymbolName(item.row.symbolId)}</CharComp>
                  </div>
                  <div class={'display-name'}>{quoteStore.getContractDisplaySymbolName(item.row.symbolId)}</div>
                </div>
              </div>
            )
          },
        },
        {
          prop: 'last',
          label: t('最新价'),
          sortable: 'custom',
          width: responsive.isMobile ? '33%' : '180px',
          render(item) {
            return (
              <div class={'price-wrap'}>
                <CharComp class={classNames('usd')}>${item.row.currency.last?.formatNumberWithCommas()}</CharComp>
                <CharComp class={'cny'}>￥{item.row.currency.cny?.formatNumberWithCommas()}</CharComp>
              </div>
            )
          },
        },
        {
          prop: 'priceChangePercent',
          label: t(isSimple.value ? '涨幅' : '24H涨跌幅'),
          align: 'right',
          sortable: 'custom',
          render(item) {
            return (
              <CharComp
                class={classNames('change-wrap', {
                  up: item.row.priceChangePercent > 0,
                  down: item.row.priceChangePercent < 0,
                })}
              >
                {item.row.priceChangePercent > 0 ? '+' : ''}
                {item.row.priceChangePercent.decimalPadZero(2)}%
              </CharComp>
            )
          },
        },
      ]
      return list
    })

    const sortMethods: Record<string, (a: SocketResponse.TradingPairData, b: SocketResponse.TradingPairData) => number> = {
      name: quoteStore.handleSortMethodBySymbolName,
      last: quoteStore.handleSortMethodByLastPrice,
      priceChangePercent: quoteStore.handleSortMethodByLastPriceChange,
      high: quoteStore.handleSortMethodByHigh,
      low: quoteStore.handleSortMethodByLow,
      '24v': quoteStore.handleSortMethodByLast24v,
    }

    const getSortData = (data: SocketResponse.TradingPairData[]) => {
      // 如果没有自定义排序，使用默认的 orderBy 升序排序

      if (!currentSortParams.value) {
        return [...data].sort((a, b) => quoteStore.handleDefaultSortMethod(a, b))
      }

      const { prop, sortType } = currentSortParams.value
      const sortFn = sortMethods[prop]

      if (!sortFn) return data

      return [...data].sort((a, b) => {
        const sortIndex = sortFn(a, b)
        return sortType === 'ascending' ? sortIndex : sortType === 'descending' ? -sortIndex : 0
      })
    }

    const onSortChange = (params: SortChangeParams) => {
      currentSortParams.value = params
    }

    const isSimple = computed(() => {
      return responsive.isMobile
    })

    watch(
      coinList,
      (newVal) => {
        moreData.init(getSortData(newVal))
      },
      { immediate: true },
    )

    onUnmounted(() => {
      quoteStore.searchKeyword = ''
    })

    // 现货table
    return () => (
      <div class="mini-quotes-container">
        <div class="search">
          <InputView class={'search-input trad-search'} height={40} radius={6} prefixIcon={Search} placeholder={firstCharToUpper(t('搜索'))} onInput={handleSearchKeywordChange} />
        </div>
        <Tabs
          v-model:active={currentMarketType.value}
          lineWidth={20}
          color="var(--colors-text-base)"
          shrink
          onClickTab={(data: { name: MarketType }) => {
            onSwitchMarket(data.name)
          }}
        >
          {marketTabs.map((item) => (
            <Tab
              class={classNames('card-tab', {
                active: item.marketType === currentMarketType.value,
              })}
              name={item.marketType}
              title={t(item.label)}
            >
              <div class={classNames('trading-pair-quotes-layout trading-pair-quotes', { sample: isSimple.value })}>
                <QuoteSkeleton loading={quoteStore.loadingContractM}>
                  <CustomTableLayout class="trading-pair-quotes-table">
                    {!responsive.isMobile && (
                      <VirtualTable
                        data={coinList.value}
                        rowHeight={60}
                        rowKey={'symbolId'}
                        expandRowKeys={expandKeys.value.map(String)}
                        onRowClick={onRowClick}
                        columns={columns.value}
                        defaultSortMethod={quoteStore.handleDefaultSortMethod}
                        isLazyLoad={false}
                        onSortChange={onSortChange}
                      />
                    )}
                    {responsive.isMobile && (
                      <VirtualTable
                        data={coinList.value}
                        rowHeight={60}
                        rowKey={'symbolId'}
                        expandRowKeys={expandKeys.value.map(String)}
                        onRowClick={onRowClick}
                        columns={columns.value}
                        defaultSortMethod={quoteStore.handleDefaultSortMethod}
                        isLazyLoad={false}
                        onSortChange={onSortChange}
                      />
                    )}
                  </CustomTableLayout>
                </QuoteSkeleton>
              </div>
            </Tab>
          ))}
        </Tabs>
      </div>
    )
  },
})
